import { ArrowRight, Shield, CheckCircle, TrendingUp, BarChart3, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const EquityTrading = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "What is the minimum amount to start equity trading?", answer: "You can start equity trading with approximately Rs.10,000. This allows you to buy shares of quality companies on NSE and BSE. We recommend starting with blue-chip stocks for beginners and gradually diversifying your portfolio." },
    { question: "What is the difference between intraday and delivery trading?", answer: "Intraday trading means buying and selling stocks on the same day - positions must be closed before market close. Delivery trading means you hold stocks for longer periods (days, months, years). Delivery trading is generally recommended for wealth creation while intraday is for experienced traders." },
    { question: "How do I open a trading account with Rumtek?", answer: "Contact us at +91 70440 55500 or visit our office. We'll help you open a demat and trading account through Sunidhi Securities. You'll need PAN card, Aadhaar, bank details, and passport-size photos. The process takes 2-3 business days." },
    { question: "Do you provide stock recommendations?", answer: "Yes, we provide research-backed stock recommendations based on fundamental and technical analysis. Our team monitors markets daily and shares actionable calls for both intraday and delivery positions. All recommendations come with entry price, target, and stop-loss levels." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.equityTrading} faqJsonLd={faqSchemas.equityTrading} />

      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-teal-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              NSE & BSE TRADING
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Equity Trading Services
              <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                NSE & BSE Since 1996
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl">
              Trade on India's premier stock exchanges with expert guidance from Rumtek Finvest. 
              As authorized persons of Sunidhi Securities & Finance Ltd, we offer seamless access to 
              NSE and BSE with research-backed recommendations, real-time market support, and 
              dedicated relationship managers for all your equity trading needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="equity-cta">
                Open Trading Account <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-emerald-600 mb-1">28+ Years</div><div className="text-gray-600 text-sm">Market Experience</div></div>
            <div><div className="text-3xl font-bold text-emerald-600 mb-1">NSE & BSE</div><div className="text-gray-600 text-sm">Both Exchanges</div></div>
            <div><div className="text-3xl font-bold text-emerald-600 mb-1">Rs.10K</div><div className="text-gray-600 text-sm">Min Investment</div></div>
            <div><div className="text-3xl font-bold text-emerald-600 mb-1">Real-Time</div><div className="text-gray-600 text-sm">Market Support</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">Our Equity Trading Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <TrendingUp className="h-7 w-7 text-white" />, title: "Delivery Trading", desc: "Long-term investment in quality stocks for wealth creation. We identify fundamentally strong companies with growth potential across large, mid, and small cap segments." },
              { icon: <Zap className="h-7 w-7 text-white" />, title: "Intraday Trading", desc: "Same-day buy-sell strategies with research-backed calls. Entry price, target, and stop-loss provided for every recommendation. Real-time support during market hours." },
              { icon: <BarChart3 className="h-7 w-7 text-white" />, title: "IPO Applications", desc: "Get notified about upcoming IPOs with our analysis on whether to apply. We help with the application process through your demat account for both mainboard and SME IPOs." },
              { icon: <Shield className="h-7 w-7 text-white" />, title: "Research & Analysis", desc: "Daily market reports, fundamental research on companies, technical analysis charts, and sector-wise analysis to help you make informed trading decisions." },
              { icon: <Target className="h-7 w-7 text-white" />, title: "Portfolio Building", desc: "We help build a diversified equity portfolio aligned with your risk profile. Sector allocation, stock selection, and periodic rebalancing for optimal returns." },
              { icon: <CheckCircle className="h-7 w-7 text-white" />, title: "Risk Management", desc: "Proper position sizing, stop-loss discipline, and portfolio hedging strategies to protect your capital. We believe in calculated risks, not reckless speculation." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">How to Get Started</h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-2xl mx-auto">Open your trading account in 3 simple steps</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "1", title: "Contact Us", desc: "Call +91 70440 55500 or visit our office with your PAN card, Aadhaar, and bank details." },
              { step: "2", title: "Account Opening", desc: "We complete your KYC and open demat + trading accounts through Sunidhi Securities in 2-3 days." },
              { step: "3", title: "Start Trading", desc: "Fund your account and start trading with our research-backed recommendations and real-time support." },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">Equity Trading FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-emerald-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['mutualFund', 'pms', 'bonds', 'commodity', 'calculator']} />

      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Trading?</h2>
          <p className="text-lg mb-8 text-emerald-100">Open your trading account today and get access to expert research and real-time market support.</p>
          <Link to="/contact" className="bg-white text-emerald-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
            Get Started <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EquityTrading;
